import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {Spinner} from 'react-bootstrap'
import {KTIcon} from 'src/marketConnect/helpers'
import {
  getPackingSlipDefaultTemplatesAsync,
  getPackingSlipShortcodesAsync,
} from 'src/redux/actions/accountActions'
import {getOrdersListAsync} from 'src/redux/actions/orderActions'
import {useAppDispatch} from 'src/redux/storeHooks'
import {
  NormalizedPackingSlipDefaultTemplate,
  NormalizedPackingSlipShortcode,
  PackingSlipShortcodeValidationState,
  buildPackingSlipCustomPreviewSrcDoc,
  computePackingSlipCustomPreviewPaperScale,
  packingSlipCssLengthToPx,
  parsePackingSlipDefaultTemplatesResponse,
  resolvePackingSlipPreviewPaperDimensions,
  parsePackingSlipShortcodesResponse,
  pickInitialPackingSlipDefaultTemplate,
  summarizePackingSlipDefaultTemplatesPayload,
  augmentPackingSlipShortcodeCatalogWithDefaultTemplates,
  validatePackingSlipTemplateShortcodes,
} from 'src/interfaces/packingSlipTemplateMappers'
import {
  REFERENCE_B2B_PACKING_SLIP_STARTER,
  mergePackingSlipDefaultTemplatesWithBuiltIn,
  resolvePackingSlipStarterTemplateHtml,
} from './packingSlipCustomStarterTemplates'

export type PackingSlipCustomWorkspaceTab = 'html' | 'preview'

export type PackingSlipCustomWorkspaceProps = {
  templateHtml: string
  onTemplateHtmlChange: (html: string) => void
  /** Sales channel platform ids used to load real orders for the preview selector. */
  platformIds?: string[]
  paperSize: string
  onPaperSizeChange?: (paperSize: string) => void
  orientation?: string
  /** True only for unsaved templates created via "New Template" (id prefix `new-packing-slip-`). */
  isNewCustomTemplate: boolean
  marginMm?: number
  blockSavingUnknownSourceCode?: boolean
  onShortcodeValidationChange?: (result: PackingSlipShortcodeValidationState) => void
}

const PACKING_SLIP_SHORTCODE_CATEGORY_ALL = '__ALL__'

const formatPackingSlipShortcodeExampleDisplay = (raw: string | undefined): string | null => {
  const trimmed = raw?.trim()
  if (!trimmed) {
    return null
  }

  if (/^e\.g\.\s/i.test(trimmed)) {
    return trimmed
  }

  return `e.g. ${trimmed}`
}

const SHORTCODE_GROUP_ORDER = [
  'ORDER',
  'BUYER',
  'ADDRESSES',
  'SHIPMENT',
  'LINE_ITEMS',
  'STORE',
  'BARCODES',
  'BARCODES_AND_LOGIC',
  'BARCODES_LOGIC',
  'OTHER',
] as const

const sortShortcodeGroups = (groups: string[]) => {
  const rank = new Map<string, number>(
    SHORTCODE_GROUP_ORDER.map((group, index) => [group, index])
  )

  return [...groups].sort((left, right) => {
    const leftRank = rank.get(left) ?? 999
    const rightRank = rank.get(right) ?? 999

    if (leftRank !== rightRank) {
      return leftRank - rightRank
    }

    return left.localeCompare(right)
  })
}

const normalizeOrientation = (value: string | undefined): 'portrait' | 'landscape' =>
  String(value ?? 'portrait').toLowerCase() === 'landscape' ? 'landscape' : 'portrait'

type WorkspaceIconName =
  | 'code'
  | 'eye'
  | 'file-code'
  | 'upload'
  | 'download'
  | 'chevron-down'

const PackingSlipWorkspaceIcon = ({name}: {name: WorkspaceIconName}) => (
  <svg
    className='packing-slip-custom-workspace__icon'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    {name === 'code' ? (
      <>
        <path d='m16 18 6-6-6-6' />
        <path d='m8 6-6 6 6 6' />
      </>
    ) : name === 'eye' ? (
      <>
        <path d='M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0' />
        <circle cx='12' cy='12' r='3' />
      </>
    ) : name === 'file-code' ? (
      <>
        <path d='M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z' />
        <polyline points='14 2 14 8 20 8' />
        <path d='m10 13-2 2 2 2' />
        <path d='m14 17 2-2-2-2' />
      </>
    ) : name === 'upload' ? (
      <>
        <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
        <polyline points='17 8 12 3 7 8' />
        <line x1='12' x2='12' y1='3' y2='15' />
      </>
    ) : name === 'download' ? (
      <>
        <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
        <polyline points='7 10 12 15 17 10' />
        <line x1='12' x2='12' y1='15' y2='3' />
      </>
    ) : (
      <path d='m6 9 6 6 6-6' />
    )}
  </svg>
)

const formatPackingSlipPreviewPaperSummary = (
  paperSizeKey: string,
  orientation: 'portrait' | 'landscape'
): string => {
  const orientationLabel = orientation === 'landscape' ? 'Landscape' : 'Portrait'

  if (paperSizeKey === 'letter') {
    return `Letter 8.5" x 11" · ${orientationLabel}`
  }

  if (paperSizeKey === 'a4') {
    return `A4 · ${orientationLabel}`
  }

  return `4" x 6" Thermal · ${orientationLabel}`
}

type PreviewOrderRow = Record<string, unknown>

const readPreviewOrderRecipient = (order: PreviewOrderRow): string => {
  const readAddressLabel = (address: unknown): string | null => {
    if (!address || typeof address !== 'object') {
      return null
    }

    const record = address as Record<string, unknown>
    const candidates = [record.company, record.name, record.first_name, record.last_name]

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim()
      }
    }

    return null
  }

  const directCandidates = [
    order.buyer_name,
    order.customer_name,
    order.recipient_name,
    order.ship_to_name,
  ]

  for (const candidate of directCandidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim()
    }
  }

  return (
    readAddressLabel(order.shipping_address) ??
    readAddressLabel(order.billing_address) ??
    'Recipient unavailable'
  )
}

const countPreviewOrderLines = (order: PreviewOrderRow): number => {
  const lineItems = order.platform_order_line_items ?? order.line_items

  if (!Array.isArray(lineItems) || !lineItems.length) {
    return 0
  }

  const totalQuantity = lineItems.reduce((sum, item) => {
    if (!item || typeof item !== 'object') {
      return sum
    }

    const quantity = Number((item as Record<string, unknown>).quantity ?? 1)
    return sum + (Number.isFinite(quantity) && quantity > 0 ? quantity : 1)
  }, 0)

  return totalQuantity || lineItems.length
}

const formatPreviewOrderSelectorLabel = (order: PreviewOrderRow): string => {
  const orderCode = String(order.api_code ?? order.api_id ?? order.id ?? 'Order')
  const prefixedCode = orderCode.startsWith('#') ? orderCode : `#${orderCode}`
  const recipient = readPreviewOrderRecipient(order)
  const lineCount = countPreviewOrderLines(order)
  const lineLabel = lineCount === 1 ? '1 line' : `${lineCount} lines`

  return `${prefixedCode} · ${recipient} · ${lineLabel}`
}

const PackingSlipCustomWorkspace = ({
  templateHtml,
  onTemplateHtmlChange,
  platformIds = [],
  paperSize,
  onPaperSizeChange,
  orientation,
  isNewCustomTemplate,
  marginMm,
  blockSavingUnknownSourceCode = true,
  onShortcodeValidationChange,
}: PackingSlipCustomWorkspaceProps) => {
  const dispatch = useAppDispatch()
  const [activeTab, setActiveTab] = useState<PackingSlipCustomWorkspaceTab>('html')
  const [shortcodeQuery, setShortcodeQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<string>(
    PACKING_SLIP_SHORTCODE_CATEGORY_ALL
  )
  const [starterMenuOpen, setStarterMenuOpen] = useState(false)

  const [defaultTemplates, setDefaultTemplates] = useState<
    NormalizedPackingSlipDefaultTemplate[]
  >([REFERENCE_B2B_PACKING_SLIP_STARTER])
  const [defaultTemplatesLoading, setDefaultTemplatesLoading] = useState(true)
  const [defaultTemplatesError, setDefaultTemplatesError] = useState<string | null>(
    null
  )

  const [shortcodes, setShortcodes] = useState<NormalizedPackingSlipShortcode[]>([])
  const [shortcodesLoading, setShortcodesLoading] = useState(true)
  const [shortcodesError, setShortcodesError] = useState<string | null>(null)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const pendingEditorCaretRef = useRef<number | null>(null)
  const gutterRef = useRef<HTMLDivElement>(null)
  const starterMenuRef = useRef<HTMLDivElement>(null)
  const previewStageRef = useRef<HTMLDivElement>(null)
  const userEditedHtmlRef = useRef(false)
  const appliedInitialStarterRef = useRef(false)

  const [previewOrders, setPreviewOrders] = useState<PreviewOrderRow[]>([])
  const [previewOrdersLoading, setPreviewOrdersLoading] = useState(false)
  const [previewOrdersError, setPreviewOrdersError] = useState<string | null>(null)
  const [selectedPreviewOrderId, setSelectedPreviewOrderId] = useState('')
  const [previewPaperScale, setPreviewPaperScale] = useState(1)

  const lineCount = useMemo(() => {
    if (!templateHtml) {
      return 1
    }
    return templateHtml.split('\n').length
  }, [templateHtml])

  const lineNumbers = useMemo(
    () => Array.from({length: lineCount}, (_, index) => index + 1),
    [lineCount]
  )

  const categoryOptions = useMemo(() => {
    const groups = sortShortcodeGroups(
      Array.from(new Set(shortcodes.map((entry) => entry.group)))
    )

    return groups.map((group) => ({
      group,
      label: shortcodes.find((entry) => entry.group === group)?.groupLabel ?? group,
    }))
  }, [shortcodes])

  useEffect(() => {
    if (
      activeCategory !== PACKING_SLIP_SHORTCODE_CATEGORY_ALL &&
      categoryOptions.length &&
      !categoryOptions.some((option) => option.group === activeCategory)
    ) {
      setActiveCategory(PACKING_SLIP_SHORTCODE_CATEGORY_ALL)
    }
  }, [activeCategory, categoryOptions])

  useEffect(() => {
    let cancelled = false

    setDefaultTemplatesLoading(true)
    setDefaultTemplatesError(null)

    dispatch(getPackingSlipDefaultTemplatesAsync() as any)
      .then((response: any) => {
        if (cancelled) {
          return
        }

        if (response?.error) {
          setDefaultTemplates([REFERENCE_B2B_PACKING_SLIP_STARTER])
          setDefaultTemplatesError('Could not load starter templates.')
          return
        }

        const parsed = parsePackingSlipDefaultTemplatesResponse(response?.payload)
        setDefaultTemplates(mergePackingSlipDefaultTemplatesWithBuiltIn(parsed))

        if (!parsed.length) {
          setDefaultTemplatesError(
            `No starter templates returned (${summarizePackingSlipDefaultTemplatesPayload(
              response?.payload
            )}).`
          )
          return
        }

        if (!parsed.some((template) => template.html.trim())) {
          setDefaultTemplatesError(
            `Starter templates returned without a mapped HTML body (${summarizePackingSlipDefaultTemplatesPayload(
              response?.payload
            )}).`
          )
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDefaultTemplates([REFERENCE_B2B_PACKING_SLIP_STARTER])
          setDefaultTemplatesError('Could not load starter templates.')
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDefaultTemplatesLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [dispatch])

  useEffect(() => {
    let cancelled = false

    setShortcodesLoading(true)
    setShortcodesError(null)

    dispatch(getPackingSlipShortcodesAsync(undefined) as any)
      .then((response: any) => {
        if (cancelled) {
          return
        }

        if (response?.error) {
          setShortcodes([])
          setShortcodesError('Could not load shortcodes.')
          return
        }

        setShortcodes(parsePackingSlipShortcodesResponse(response?.payload))
      })
      .catch(() => {
        if (!cancelled) {
          setShortcodes([])
          setShortcodesError('Could not load shortcodes.')
        }
      })
      .finally(() => {
        if (!cancelled) {
          setShortcodesLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [dispatch])

  useEffect(() => {
    if (!isNewCustomTemplate) {
      return
    }

    if (appliedInitialStarterRef.current) {
      return
    }

    if (userEditedHtmlRef.current) {
      return
    }

    if (templateHtml.trim()) {
      return
    }

    if (defaultTemplatesLoading) {
      return
    }

    const initialTemplate = pickInitialPackingSlipDefaultTemplate(defaultTemplates)
    if (!initialTemplate) {
      return
    }

    appliedInitialStarterRef.current = true
    if (initialTemplate.id === REFERENCE_B2B_PACKING_SLIP_STARTER.id) {
      onPaperSizeChange?.('letter')
    }
    onTemplateHtmlChange(resolvePackingSlipStarterTemplateHtml(initialTemplate))
  }, [
    defaultTemplates,
    defaultTemplatesLoading,
    isNewCustomTemplate,
    onPaperSizeChange,
    onTemplateHtmlChange,
    templateHtml,
  ])

  const handleTemplateHtmlChange = useCallback(
    (html: string) => {
      userEditedHtmlRef.current = true
      onTemplateHtmlChange(html)
    },
    [onTemplateHtmlChange]
  )

  useEffect(() => {
    if (!starterMenuOpen) {
      return
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!starterMenuRef.current?.contains(event.target as Node)) {
        setStarterMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [starterMenuOpen])

  useEffect(() => {
    if (!starterMenuOpen) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }

      setStarterMenuOpen(false)
      event.preventDefault()
      event.stopPropagation()
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [starterMenuOpen])

  const syncGutterScroll = useCallback(() => {
    const textarea = textareaRef.current
    const gutter = gutterRef.current
    if (!textarea || !gutter) {
      return
    }
    gutter.scrollTop = textarea.scrollTop
  }, [])

  const filteredShortcodes = useMemo(() => {
    const query = shortcodeQuery.trim().toLowerCase()

    return shortcodes.filter((entry) => {
      if (
        activeCategory !== PACKING_SLIP_SHORTCODE_CATEGORY_ALL &&
        entry.group !== activeCategory
      ) {
        return false
      }

      if (!query) {
        return true
      }

      const searchable = [entry.shortcode, entry.label, entry.description, entry.exampleValue]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return searchable.includes(query)
    })
  }, [activeCategory, shortcodeQuery, shortcodes])

  const groupedFilteredShortcodes = useMemo(() => {
    const groups = sortShortcodeGroups(
      Array.from(new Set(filteredShortcodes.map((entry) => entry.group)))
    )

    return groups.map((group) => ({
      group,
      groupLabel:
        filteredShortcodes.find((entry) => entry.group === group)?.groupLabel ?? group,
      entries: filteredShortcodes.filter((entry) => entry.group === group),
    }))
  }, [filteredShortcodes])

  const insertShortcodeAtCursor = useCallback(
    (token: string) => {
      const textarea = textareaRef.current

      if (!textarea) {
        handleTemplateHtmlChange(`${templateHtml}${token}`)
        return
      }

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const nextValue = `${templateHtml.slice(0, start)}${token}${templateHtml.slice(end)}`
      pendingEditorCaretRef.current = start + token.length
      handleTemplateHtmlChange(nextValue)
    },
    [handleTemplateHtmlChange, templateHtml]
  )

  useEffect(() => {
    const caret = pendingEditorCaretRef.current
    if (caret === null) {
      return
    }

    pendingEditorCaretRef.current = null
    const textarea = textareaRef.current
    if (!textarea) {
      return
    }

    requestAnimationFrame(() => {
      textarea.focus()
      textarea.setSelectionRange(caret, caret)
    })
  }, [templateHtml])

  const handleStarterTemplateSelect = (template: NormalizedPackingSlipDefaultTemplate) => {
    if (template.id === REFERENCE_B2B_PACKING_SLIP_STARTER.id) {
      onPaperSizeChange?.('letter')
    }
    handleTemplateHtmlChange(resolvePackingSlipStarterTemplateHtml(template))
    setStarterMenuOpen(false)
  }

  const handleHtmlFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''

    if (!file) {
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        handleTemplateHtmlChange(reader.result)
      }
    }
    reader.readAsText(file)
  }

  const {paperWidth, paperHeight, paperSizeKey} = resolvePackingSlipPreviewPaperDimensions(
    paperSize,
    orientation
  )
  const previewOrientation = normalizeOrientation(orientation)

  const shortcodesForValidation = useMemo(
    () =>
      augmentPackingSlipShortcodeCatalogWithDefaultTemplates(shortcodes, defaultTemplates),
    [defaultTemplates, shortcodes]
  )

  const shortcodeValidation = useMemo(
    () => validatePackingSlipTemplateShortcodes(templateHtml, shortcodesForValidation),
    [shortcodesForValidation, templateHtml]
  )

  useEffect(() => {
    onShortcodeValidationChange?.({
      ...shortcodeValidation,
      catalogCheckComplete: !shortcodesLoading,
    })
  }, [onShortcodeValidationChange, shortcodeValidation, shortcodesLoading])

  const paperWidthPx = useMemo(() => packingSlipCssLengthToPx(paperWidth), [paperWidth])
  const paperHeightPx = useMemo(() => packingSlipCssLengthToPx(paperHeight), [paperHeight])

  const previewSrcDoc = useMemo(
    () =>
      buildPackingSlipCustomPreviewSrcDoc({
        html: templateHtml,
        paperWidth,
        paperHeight,
        marginMm,
        paperWidthPx,
      }),
    [marginMm, paperHeight, paperWidth, paperWidthPx, templateHtml]
  )
  const previewPaperSummary = useMemo(
    () =>
      formatPackingSlipPreviewPaperSummary(
        paperSizeKey,
        previewOrientation
      ),
    [paperSizeKey, previewOrientation]
  )

  const previewPlatformId = platformIds.find((id) => String(id).trim()) ?? ''

  const selectedPreviewOrder = useMemo(
    () =>
      previewOrders.find((order) => String(order.id) === selectedPreviewOrderId) ?? null,
    [previewOrders, selectedPreviewOrderId]
  )

  useEffect(() => {
    if (activeTab !== 'preview') {
      return
    }

    const stage = previewStageRef.current
    if (!stage) {
      return
    }

    const updateScale = () => {
      const nextScale = computePackingSlipCustomPreviewPaperScale({
        paperWidthPx,
        paperHeightPx,
        stageClientWidth: stage.clientWidth,
      })
      setPreviewPaperScale(nextScale > 0 ? nextScale : 1)
    }

    updateScale()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateScale)
      return () => window.removeEventListener('resize', updateScale)
    }

    const observer = new ResizeObserver(() => updateScale())
    observer.observe(stage)
    return () => observer.disconnect()
  }, [activeTab, paperHeightPx, paperWidthPx])

  useEffect(() => {
    if (activeTab !== 'preview') {
      return
    }

    if (!previewPlatformId) {
      setPreviewOrders([])
      setPreviewOrdersError(null)
      setSelectedPreviewOrderId('')
      return
    }

    let cancelled = false
    setPreviewOrdersLoading(true)
    setPreviewOrdersError(null)

    dispatch(
      getOrdersListAsync({
        paramData: {
          page: 1,
          limit: 25,
          sort_by: 'api_created_at',
          sort_order: 'DESC',
          status_filters: {},
          api_created_at_min: '',
          api_created_at_max: '',
        },
        selectedPlatform: String(previewPlatformId),
      }) as any
    )
      .then((response: any) => {
        if (cancelled) {
          return
        }

        if (response?.error) {
          setPreviewOrders([])
          setPreviewOrdersError('Could not load orders for preview.')
          setSelectedPreviewOrderId('')
          return
        }

        const payload = response?.payload ?? response
        const results = Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.data)
            ? payload.data
            : []

        setPreviewOrders(results as PreviewOrderRow[])
        setPreviewOrdersError(null)

        if (results.length) {
          setSelectedPreviewOrderId(String((results[0] as PreviewOrderRow).id))
        } else {
          setSelectedPreviewOrderId('')
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreviewOrders([])
          setPreviewOrdersError('Could not load orders for preview.')
          setSelectedPreviewOrderId('')
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPreviewOrdersLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [activeTab, dispatch, previewPlatformId])

  const templateValidity = useMemo(() => {
    if (shortcodesLoading) {
      return {
        tone: 'pending' as const,
        label: 'Checking template…',
        title: 'Shortcode catalogue is loading.',
      }
    }

    if (!shortcodeValidation.catalogLoaded) {
      if (blockSavingUnknownSourceCode) {
        return {
          tone: 'invalid' as const,
          label: shortcodesLoading ? 'Validating…' : 'Validation unavailable · save blocked',
          title: shortcodesLoading
            ? 'Shortcode catalogue is loading.'
            : 'Shortcode catalogue did not load. Saving is blocked until shortcodes can be verified.',
        }
      }

      return {
        tone: 'neutral' as const,
        label: 'Validation unavailable',
        title: 'Shortcode catalogue did not load; template validity was not checked.',
      }
    }

    if (shortcodeValidation.unknownTokens.length) {
      const count = shortcodeValidation.unknownTokens.length
      return {
        tone: 'invalid' as const,
        label: `Template invalid · ${count} unrecognized`,
        title: shortcodeValidation.unknownTokens.join(', '),
      }
    }

    return {
      tone: 'valid' as const,
      label: 'Template valid',
      title: 'All detected Liquid-style shortcodes match the catalogue.',
    }
  }, [blockSavingUnknownSourceCode, shortcodeValidation, shortcodesLoading])

  const editorStatus = useMemo(() => {
    if (shortcodesLoading) {
      if (blockSavingUnknownSourceCode) {
        return {
          tone: 'warning' as const,
          message: 'Loading shortcode catalogue — saving is blocked until validation finishes.',
        }
      }

      return {
        tone: 'ready' as const,
        message: 'Ready to edit',
      }
    }

    if (shortcodesError || !shortcodeValidation.catalogLoaded) {
      if (blockSavingUnknownSourceCode) {
        return {
          tone: 'error' as const,
          message:
            'Shortcode catalogue unavailable — saving is blocked until shortcodes can be verified.',
        }
      }

      return {
        tone: 'ready' as const,
        message:
          'Ready to edit — shortcode catalogue unavailable; validation skipped until shortcodes load.',
      }
    }

    if (shortcodeValidation.unknownTokens.length) {
      const preview = shortcodeValidation.unknownTokens.join(', ')

      if (blockSavingUnknownSourceCode) {
        return {
          tone: 'error' as const,
          message: `Unknown shortcodes block save: ${preview}`,
        }
      }

      return {
        tone: 'warning' as const,
        message: `Unknown shortcodes (save allowed): ${preview}`,
      }
    }

    return {
      tone: 'ready' as const,
      message: 'Ready to edit',
    }
  }, [
    blockSavingUnknownSourceCode,
    shortcodeValidation,
    shortcodesError,
    shortcodesLoading,
  ])

  const importToolbar = (
    <>
      <div className='packing-slip-custom-workspace__starter' ref={starterMenuRef}>
        <button
          type='button'
          className='packing-slip-custom-workspace__starter-trigger'
          aria-expanded={starterMenuOpen}
          aria-haspopup='menu'
          onClick={() => setStarterMenuOpen((open) => !open)}
        >
          Start from template
          <PackingSlipWorkspaceIcon name='chevron-down' />
        </button>
        {starterMenuOpen ? (
          <div className='packing-slip-custom-workspace__starter-menu' role='menu'>
            {defaultTemplatesLoading ? (
              <div className='packing-slip-custom-workspace__starter-status'>
                <Spinner animation='border' size='sm' />
                <span>Loading templates…</span>
              </div>
            ) : null}
            {!defaultTemplatesLoading && defaultTemplatesError ? (
              <p className='packing-slip-custom-workspace__starter-status is-error'>
                {defaultTemplatesError}
              </p>
            ) : null}
            {!defaultTemplatesLoading && !defaultTemplatesError && !defaultTemplates.length ? (
              <p className='packing-slip-custom-workspace__starter-status'>
                No starter templates are available yet.
              </p>
            ) : null}
            {!defaultTemplatesLoading &&
              defaultTemplates.map((option) => (
                <button
                  key={option.id}
                  type='button'
                  role='menuitem'
                  className='packing-slip-custom-workspace__starter-item'
                  onClick={() => handleStarterTemplateSelect(option)}
                >
                  {option.label}
                  {!option.html.trim() ? ' (no HTML in response)' : ''}
                </button>
              ))}
            <span className='packing-slip-custom-workspace__starter-hint'>
              Replaces the current editor content.
            </span>
          </div>
        ) : null}
      </div>

      <label className='packing-slip-custom-workspace__upload'>
        <PackingSlipWorkspaceIcon name='upload' />
        Upload .html
        <input type='file' accept='.html,.htm,text/html' onChange={handleHtmlFileUpload} />
      </label>
    </>
  )

  const paperDisplayHeight = paperHeightPx

  return (
    <div className='packing-slip-custom-workspace'>
      <div className='packing-slip-custom-workspace__header'>
        <div className='packing-slip-custom-workspace__header-start'>
          <span className='packing-slip-custom-workspace__title'>CUSTOM HTML TEMPLATE</span>
          <div
            className='packing-slip-custom-workspace__tabs'
            role='group'
            aria-label='Workspace view'
          >
            <button
              type='button'
              className={`packing-slip-custom-workspace__tab ${
                activeTab === 'html' ? 'is-active' : ''
              }`}
              aria-pressed={activeTab === 'html'}
              onClick={() => setActiveTab('html')}
            >
              <PackingSlipWorkspaceIcon name='code' />
              HTML
            </button>
            <button
              type='button'
              className={`packing-slip-custom-workspace__tab ${
                activeTab === 'preview' ? 'is-active' : ''
              }`}
              aria-pressed={activeTab === 'preview'}
              onClick={() => setActiveTab('preview')}
            >
              <PackingSlipWorkspaceIcon name='eye' />
              Preview
            </button>
          </div>
        </div>
        <div className='packing-slip-custom-workspace__header-actions'>{importToolbar}</div>
      </div>

      {activeTab === 'html' ? (
        <>
          <div className='packing-slip-custom-workspace__html-layout'>
            <div className='packing-slip-code-editor'>
              <div className='packing-slip-code-editor__filename-bar'>
                <span className='packing-slip-code-editor__filename'>packing-slip.html</span>
                <span className='packing-slip-code-editor__meta'>
                  {lineCount} lines · Liquid-style shortcodes
                </span>
              </div>

              <div className='packing-slip-code-editor__body'>
                <div
                  ref={gutterRef}
                  className='packing-slip-code-editor__gutter'
                  aria-hidden='true'
                >
                  {lineNumbers.map((lineNumber) => (
                    <div key={lineNumber} className='packing-slip-code-editor__line-number'>
                      {lineNumber}
                    </div>
                  ))}
                  <div className='packing-slip-code-editor__gutter-spacer' />
                </div>

                <textarea
                  ref={textareaRef}
                  className='packing-slip-code-editor__textarea'
                  value={templateHtml}
                  onChange={(event) => handleTemplateHtmlChange(event.target.value)}
                  onScroll={syncGutterScroll}
                  spellCheck={false}
                  wrap='off'
                  aria-label='Template HTML'
                />
              </div>

              <div
                className={`packing-slip-code-editor__status is-${editorStatus.tone}`}
                role='status'
              >
                <span className='packing-slip-code-editor__status-dot' aria-hidden='true'>
                  ●
                </span>
                <span>{editorStatus.message}</span>
              </div>
            </div>

            <aside className='packing-slip-shortcodes-panel' aria-label='Add order details'>
              <div className='packing-slip-shortcodes-panel__head'>
                <div>
                  <span className='packing-slip-shortcodes-panel__title'>Add order details</span>
                  <p className='packing-slip-shortcodes-panel__subtitle'>
                    Click in your design where the detail should appear, then pick one below. Each
                    order&apos;s real value is filled in when the slip prints.
                  </p>
                </div>

                <div className='packing-slip-shortcodes-panel__search-wrap'>
                  <KTIcon iconName='magnifier' className='packing-slip-shortcodes-panel__search-icon fs-6' />
                  <input
                    type='search'
                    className='packing-slip-shortcodes-panel__search'
                    aria-label='Search shortcodes'
                    placeholder='Search e.g. PO, UPC, tracking'
                    value={shortcodeQuery}
                    onChange={(event) => setShortcodeQuery(event.target.value)}
                  />
                </div>

                {shortcodesLoading ? (
                  <div className='packing-slip-shortcodes-panel__loading'>
                    <Spinner animation='border' size='sm' />
                    <span>Loading shortcodes…</span>
                  </div>
                ) : null}

                {!shortcodesLoading && shortcodesError ? (
                  <p className='packing-slip-shortcodes-panel__error'>{shortcodesError}</p>
                ) : null}

                {!shortcodesLoading && !shortcodesError && categoryOptions.length ? (
                  <div className='packing-slip-shortcodes-panel__chips'>
                    <button
                      type='button'
                      className={`packing-slip-shortcodes-panel__chip ${
                        activeCategory === PACKING_SLIP_SHORTCODE_CATEGORY_ALL ? 'is-active' : ''
                      }`}
                      aria-pressed={activeCategory === PACKING_SLIP_SHORTCODE_CATEGORY_ALL}
                      onClick={() => setActiveCategory(PACKING_SLIP_SHORTCODE_CATEGORY_ALL)}
                    >
                      All
                    </button>
                    {categoryOptions.map((category) => (
                      <button
                        key={category.group}
                        type='button'
                        className={`packing-slip-shortcodes-panel__chip ${
                          activeCategory === category.group ? 'is-active' : ''
                        }`}
                        aria-pressed={activeCategory === category.group}
                        onClick={() => setActiveCategory(category.group)}
                      >
                        {category.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className='packing-slip-shortcodes-panel__list'>
                {shortcodesLoading ? null : shortcodesError ? null : groupedFilteredShortcodes.length ? (
                  <div className='packing-slip-shortcodes-panel__groups'>
                    {groupedFilteredShortcodes.map((groupSection) => (
                      <section
                        key={groupSection.group}
                        className='packing-slip-shortcodes-panel__group'
                        aria-label={groupSection.groupLabel}
                      >
                        <h3 className='packing-slip-shortcodes-panel__group-heading'>
                          {groupSection.group}
                        </h3>
                        <ul className='packing-slip-shortcodes-panel__items'>
                          {groupSection.entries.map((entry) => {
                            const exampleDisplay = formatPackingSlipShortcodeExampleDisplay(
                              entry.exampleValue
                            )
                            const title =
                              entry.label?.trim() ||
                              entry.shortcode.replace(/\{\{|\}\}/g, '').trim() ||
                              'Shortcode'

                            return (
                              <li key={`${entry.group}:${entry.shortcode}`}>
                                <div className='packing-slip-shortcodes-panel__item'>
                                  <div className='packing-slip-shortcodes-panel__item-body'>
                                    <span className='packing-slip-shortcodes-panel__item-label'>
                                      {title}
                                    </span>
                                    {exampleDisplay ? (
                                      <span
                                        className='packing-slip-shortcodes-panel__item-example'
                                        title={exampleDisplay}
                                      >
                                        {exampleDisplay}
                                      </span>
                                    ) : null}
                                  </div>
                                  <button
                                    type='button'
                                    className='packing-slip-shortcodes-panel__item-add'
                                    aria-label={`Insert ${title}`}
                                    onClick={() => insertShortcodeAtCursor(entry.shortcode)}
                                  >
                                    <KTIcon iconName='plus' className='fs-5' />
                                  </button>
                                </div>
                              </li>
                            )
                          })}
                        </ul>
                      </section>
                    ))}
                  </div>
                ) : (
                  <p className='packing-slip-shortcodes-panel__empty'>
                    {shortcodeQuery.trim()
                      ? 'No shortcodes match your search.'
                      : 'No shortcodes are available for this category yet.'}
                  </p>
                )}
              </div>
            </aside>
          </div>
        </>
      ) : (
        <div className='packing-slip-custom-workspace__preview'>
          <div className='packing-slip-custom-preview-toolbar'>
            <div className='packing-slip-custom-preview-toolbar__row'>
              <div className='packing-slip-custom-preview-toolbar__order'>
                <label
                  className='packing-slip-custom-preview-toolbar__order-label'
                  htmlFor='packing-slip-preview-order-select'
                >
                  Preview with order
                </label>
                <div className='packing-slip-custom-preview-toolbar__order-select-wrap'>
                  {previewOrdersLoading ? (
                    <Spinner animation='border' size='sm' aria-hidden='true' />
                  ) : null}
                  <select
                    id='packing-slip-preview-order-select'
                    className='packing-slip-custom-preview-toolbar__order-select'
                    value={selectedPreviewOrderId}
                    disabled={
                      !previewPlatformId ||
                      previewOrdersLoading ||
                      !previewOrders.length
                    }
                    onChange={(event) => setSelectedPreviewOrderId(event.target.value)}
                    aria-describedby='packing-slip-preview-order-hint'
                  >
                    {!platformIds.some((id) => String(id).trim()) ? (
                      <option value=''>Select a sales channel first</option>
                    ) : previewOrdersLoading ? (
                      <option value=''>Loading orders…</option>
                    ) : previewOrdersError ? (
                      <option value=''>Orders unavailable</option>
                    ) : !previewOrders.length ? (
                      <option value=''>No orders found for this channel</option>
                    ) : (
                      previewOrders.map((order) => (
                        <option key={String(order.id)} value={String(order.id)}>
                          {formatPreviewOrderSelectorLabel(order)}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              <div className='packing-slip-custom-preview-toolbar__meta'>
                <span className='packing-slip-custom-preview-toolbar__paper'>
                  {previewPaperSummary}
                  {marginMm ? ` · ${marginMm} mm margin` : ''}
                </span>
                <span
                  className={`packing-slip-custom-preview-toolbar__validity is-${templateValidity.tone}`}
                  title={templateValidity.title}
                >
                  {templateValidity.label}
                </span>
              </div>

              <button
                type='button'
                className='packing-slip-custom-preview-toolbar__download'
                disabled
                title='Custom HTML test PDF rendering is not available yet. Existing /api/order-packing-slip-download only serves standard order documents, not custom Liquid templates.'
              >
                <PackingSlipWorkspaceIcon name='download' />
                Download test PDF
              </button>
            </div>
            <p
              id='packing-slip-preview-order-hint'
              className='packing-slip-custom-preview-toolbar__order-hint'
            >
              {previewOrdersError
                ? previewOrdersError
                : !platformIds.some((id) => String(id).trim())
                  ? 'Assign at least one channel under Template For to load orders from /api/platform-orders-list.'
                  : selectedPreviewOrder
                    ? `Selected order metadata only — Liquid shortcodes in the HTML are not replaced in this preview.`
                    : 'Choose an order to show real order metadata alongside the layout preview.'}
            </p>
          </div>

          <div
            ref={previewStageRef}
            className={`packing-slip-custom-preview-stage is-${previewOrientation}`}
          >
            {templateHtml.trim() ? (
              <div
                className='packing-slip-custom-preview-stage__scaler'
                style={{
                  width: paperWidthPx * previewPaperScale,
                  height: paperDisplayHeight * previewPaperScale,
                }}
              >
                <div
                  className='packing-slip-custom-preview-paper'
                  style={{
                    width: paperWidthPx,
                    height: paperHeightPx,
                    minHeight: paperHeightPx,
                    transform: `scale(${previewPaperScale})`,
                    transformOrigin: 'top left',
                  }}
                >
                  <iframe
                    title='Custom packing slip HTML preview'
                    className='packing-slip-custom-preview-iframe'
                    sandbox=''
                    scrolling='no'
                    srcDoc={previewSrcDoc}
                    style={{
                      width: paperWidthPx,
                      height: paperHeightPx,
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className='packing-slip-custom-preview-paper packing-slip-custom-preview-paper--empty'>
                <div className='packing-slip-custom-preview-paper__placeholder'>
                  <KTIcon iconName='document' className='fs-2x text-muted mb-3' />
                  <span className='fw-semibold fs-6 text-gray-700'>No HTML to preview</span>
                  <p className='fs-7 text-muted mb-0 mt-2'>
                    Add template HTML on the HTML tab to see a sandboxed preview here.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export {PackingSlipCustomWorkspace}
export default PackingSlipCustomWorkspace
